import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Users,
  AlertTriangle,
  TrendingUp,
  Percent,
  Search,
  Info,
  GraduationCap,
  UserX,
} from 'lucide-react';
import { RelatorioAlunoDetalhePanel } from './RelatorioAlunoDetalhePanel';
import {
  corFrequencia,
  iniciaisNome,
} from './relatorios-professor-shared';
import type { RelatoriosProfessorPageContext } from './useRelatoriosProfessorPage';
import './relatorios-professor-desktop.css';
import './professor-dashboard-desktop.css';

interface Props {
  ctx: RelatoriosProfessorPageContext;
}

export function RelatoriosProfessorDesktop({ ctx }: Props) {
  const navigate = useNavigate();
  const {
    minhasTurmas,
    selectedTurmaId,
    setSelectedTurmaId,
    selectedAlunoId,
    setSelectedAlunoId,
    alunoSelecionado,
    turmaSelecionada,
    somenteRisco,
    setSomenteRisco,
    freq,
    cons,
    alunosExibidos,
    isLoading,
    isLoadingCons,
  } = ctx;

  const [busca, setBusca] = useState('');

  useEffect(() => {
    if (minhasTurmas.length > 0 && !selectedTurmaId) {
      setSelectedTurmaId(minhasTurmas[0].id);
    }
  }, [minhasTurmas, selectedTurmaId, setSelectedTurmaId]);

  useEffect(() => {
    setSelectedAlunoId(null);
  }, [selectedTurmaId, somenteRisco, setSelectedAlunoId]);

  const filtered = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return alunosExibidos;
    return alunosExibidos.filter(
      (a) =>
        a.nome.toLowerCase().includes(q) ||
        (a.matricula || '').toLowerCase().includes(q),
    );
  }, [alunosExibidos, busca]);

  useEffect(() => {
    if (filtered.length === 0) {
      setSelectedAlunoId(null);
      return;
    }
    if (!selectedAlunoId || !filtered.some((a) => a.alunoId === selectedAlunoId)) {
      setSelectedAlunoId(filtered[0].alunoId);
    }
  }, [filtered, selectedAlunoId, setSelectedAlunoId]);

  const toggleSomenteRisco = () => setSomenteRisco(!somenteRisco);

  return (
    <div className="admin-page text-[13px] leading-snug dash-desk-page rel-desk-page">
      <div className="dash-desk-root rel-desk-root">
        <div className="dash-desk-top rel-desk-top">
          <div className="dash-desk-greeting">
            <h1 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
              Relatorios de frequencia
            </h1>
            <p className="rel-desk-subtitle">
              Suas turmas · consolidacao escopada ao professor
            </p>
          </div>
          <BarChart3 size={18} style={{ color: '#2563eb', opacity: 0.85 }} aria-hidden />
        </div>

        <div className="rel-desk-metrics">
          {cons ? (
            <>
              <div className="dash-desk-kpi">
                <div className="dash-desk-kpi-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
                  <Users size={15} />
                </div>
                <div>
                  <div className="dash-desk-kpi-val">{cons.resumo.totalAlunos}</div>
                  <div className="dash-desk-kpi-lbl">Meus alunos</div>
                </div>
              </div>
              <div className="dash-desk-kpi">
                <div className="dash-desk-kpi-icon" style={{ background: '#ecfdf5', color: '#059669' }}>
                  <Percent size={15} />
                </div>
                <div>
                  <div className="dash-desk-kpi-val">{cons.resumo.mediaFrequencia}%</div>
                  <div className="dash-desk-kpi-lbl">Frequencia media</div>
                </div>
              </div>
              <button
                type="button"
                className={`rel-desk-kpi-btn${somenteRisco ? ' rel-desk-kpi-btn--active' : ''}`}
                onClick={toggleSomenteRisco}
                disabled={!selectedTurmaId}
              >
                <div className="dash-desk-kpi">
                  <div className="dash-desk-kpi-icon" style={{ background: '#fef2f2', color: '#dc2626' }}>
                    <UserX size={15} />
                  </div>
                  <div>
                    <div className="dash-desk-kpi-val">
                      {freq?.alunosEmRisco ?? cons.resumo.alunosEmRisco ?? 0}
                    </div>
                    <div className="dash-desk-kpi-lbl">Em risco</div>
                  </div>
                </div>
              </button>
              <div className="dash-desk-kpi">
                <div className="dash-desk-kpi-icon" style={{ background: '#f5f3ff', color: '#7c3aed' }}>
                  <TrendingUp size={15} />
                </div>
                <div>
                  <div className="dash-desk-kpi-val">{cons.resumo.totalAvaliacoes}</div>
                  <div className="dash-desk-kpi-lbl">Minhas avaliacoes</div>
                </div>
              </div>
            </>
          ) : isLoadingCons ? (
            <p className="rel-desk-loading">Carregando indicadores…</p>
          ) : null}
        </div>

        {freq && selectedTurmaId && (
          <div className="rel-desk-turma-kpis">
            <div className="dash-desk-kpi rel-desk-turma-kpi">
              <div className="dash-desk-kpi-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
                <GraduationCap size={15} />
              </div>
              <div>
                <div className="dash-desk-kpi-val">{freq.totalAlunos}</div>
                <div className="dash-desk-kpi-lbl">Alunos na turma</div>
              </div>
            </div>
            <div className="dash-desk-kpi rel-desk-turma-kpi">
              <div
                className="dash-desk-kpi-icon"
                style={{ background: '#ecfdf5', color: corFrequencia(freq.mediaFrequencia) }}
              >
                <Percent size={15} />
              </div>
              <div>
                <div className="dash-desk-kpi-val" style={{ color: corFrequencia(freq.mediaFrequencia) }}>
                  {freq.mediaFrequencia}%
                </div>
                <div className="dash-desk-kpi-lbl">Media da turma</div>
              </div>
            </div>
            <div className="dash-desk-kpi rel-desk-turma-kpi">
              <div className="dash-desk-kpi-icon" style={{ background: '#fef2f2', color: '#dc2626' }}>
                <AlertTriangle size={15} />
              </div>
              <div>
                <div className="dash-desk-kpi-val">{freq.alunosEmRisco}</div>
                <div className="dash-desk-kpi-lbl">Risco na turma</div>
              </div>
            </div>
          </div>
        )}

        <div className="rel-desk-notice">
          <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            <strong>Painel de frequencia</strong> — priorize alunos abaixo de 75% (LDB). Compare
            bimestres antes de reunioes e abra a ficha do aluno no painel.
          </span>
        </div>

        <div className="rel-desk-toolbar">
          <div className="rel-desk-search-wrap">
            <Search size={14} />
            <input
              type="search"
              className="rel-desk-search"
              placeholder="Buscar aluno ou matricula…"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              disabled={!selectedTurmaId}
            />
          </div>
          <select
            className="rel-desk-filter"
            value={selectedTurmaId}
            onChange={(e) => setSelectedTurmaId(e.target.value)}
            aria-label="Turma"
          >
            <option value="">Turma</option>
            {minhasTurmas.map((t) => (
              <option key={t.id} value={t.id}>
                {t.nome}
                {t.serie ? ` (${t.serie})` : ''}
              </option>
            ))}
          </select>
          <label className="rel-desk-check">
            <input
              type="checkbox"
              checked={somenteRisco}
              onChange={(e) => setSomenteRisco(e.target.checked)}
              disabled={!selectedTurmaId}
            />
            Somente risco
          </label>
          <span className="rel-desk-count">
            {filtered.length} aluno{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="rel-desk-body">
          <div className="rel-desk-list-card">
            <div className="rel-desk-list-scroll">
              {!selectedTurmaId ? (
                <p className="rel-desk-loading">Selecione uma turma.</p>
              ) : isLoading ? (
                <p className="rel-desk-loading">Carregando frequencia…</p>
              ) : !freq ? (
                <p className="rel-desk-loading">Sem dados para esta turma.</p>
              ) : filtered.length === 0 ? (
                <p className="rel-desk-loading">Nenhum aluno neste filtro.</p>
              ) : (
                filtered.map((aluno) => {
                  const active = selectedAlunoId === aluno.alunoId;
                  const cor = corFrequencia(aluno.percentual);
                  return (
                    <button
                      key={aluno.alunoId}
                      type="button"
                      className={`rel-desk-list-item${active ? ' rel-desk-list-item--active' : ''}${
                        aluno.emRisco ? ' rel-desk-list-item--risco' : ''
                      }`}
                      onClick={() => setSelectedAlunoId(aluno.alunoId)}
                    >
                      <div
                        className="rel-desk-list-avatar"
                        style={{
                          background: aluno.emRisco
                            ? 'rgba(220,38,38,0.1)'
                            : 'linear-gradient(135deg, #1e3a5f, #2563eb)',
                          color: aluno.emRisco ? '#dc2626' : '#fff',
                        }}
                      >
                        {aluno.emRisco ? (
                          <AlertTriangle size={14} />
                        ) : (
                          iniciaisNome(aluno.nome)
                        )}
                      </div>
                      <div className="rel-desk-list-text">
                        <span className="rel-desk-list-title">{aluno.nome}</span>
                        <span className="rel-desk-list-meta">
                          {aluno.presentes}/{aluno.totalDias} dias
                          {aluno.emRisco ? ' · Risco' : ''}
                        </span>
                      </div>
                      <span className="rel-desk-list-pct" style={{ color: cor }}>
                        {aluno.percentual}%
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="rel-desk-detail-card">
            <div className="rel-desk-detail-scroll">
              {!selectedTurmaId ? (
                <div className="rel-desk-detail-empty">
                  <GraduationCap size={32} style={{ opacity: 0.25 }} />
                  <p>Escolha uma turma para ver a frequencia dos alunos.</p>
                </div>
              ) : isLoading ? (
                <p className="rel-desk-loading">Carregando…</p>
              ) : alunoSelecionado ? (
                <RelatorioAlunoDetalhePanel
                  aluno={alunoSelecionado}
                  turmaNome={turmaSelecionada?.nome}
                  mediaTurma={freq?.mediaFrequencia}
                  onVerFicha={(id) => navigate(`/professor/alunos/${id}`)}
                />
              ) : (
                <div className="rel-desk-detail-empty">
                  <Users size={32} style={{ opacity: 0.25 }} />
                  <p>Nenhum aluno no filtro atual.</p>
                </div>
              )}
            </div>
            {freq && (
              <div className="rel-desk-legenda">
                <span>
                  <span className="rel-desk-legenda-dot" style={{ background: '#059669' }} />
                  Adequado 80%+
                </span>
                <span>
                  <span className="rel-desk-legenda-dot" style={{ background: '#d97706' }} />
                  Atencao
                </span>
                <span>
                  <span className="rel-desk-legenda-dot" style={{ background: '#dc2626' }} />
                  Risco &lt;75%
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
